import React from "react";
import PropTypes from "prop-types";
import { Box, ButtonBase, Radio, Typography } from "@mui/material";
import { Building2, Search, UserRound, Users, X } from "lucide-react";

import { Checkbox } from "../../../components/Checkbox";
import { Label } from "../../../components/Label";
import { useTheme } from "../../../theme/useTheme";
import {
  AGENCY_SCOPE_ALL,
  AGENCY_SCOPE_EXTERNAL,
  AGENCY_SCOPE_MANUAL,
  AGENCY_SCOPE_SERSEO,
  CLIENT_MODE_ALL,
  CLIENT_MODE_NONE,
  CLIENT_MODE_SELECTED,
  filterClientsByAgencyIds,
  resolveAudienceAgencyIds,
} from "./announcementAudience";

const AGENCY_TYPE_TAG = {
  [AGENCY_SCOPE_SERSEO]: "SERSEO",
  [AGENCY_SCOPE_EXTERNAL]: "Externa",
};

const normalize = (text) =>
  (text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const plural = (count, singular, pluralForm) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

const TextLinkButton = ({ children, onClick, disabled }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      fontSize: "0.8125rem",
      fontWeight: 500,
      color: "text.secondary",
      borderRadius: "6px",
      px: 0.5,
      "&:hover": { color: "text.primary", textDecoration: "underline" },
      "&.Mui-disabled": { opacity: 0.45 },
    }}
  >
    {children}
  </ButtonBase>
);

TextLinkButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

/** Lista plana con buscador; los seleccionados al abrir quedan arriba. */
const SelectionList = ({
  items,
  selectedIds,
  onToggle,
  onSelectMany,
  onClear,
  searchPlaceholder,
  emptyText,
  loadingText,
  isLoading,
  selectedWord,
  allLabel,
  noneLabel,
  maxHeight = 260,
}) => {
  const { theme } = useTheme();
  const [query, setQuery] = React.useState("");
  const [pinnedIds] = React.useState(() => new Set(selectedIds));
  const listRef = React.useRef(null);

  React.useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [query]);

  const orderedItems = React.useMemo(
    () =>
      [...items].sort((a, b) => {
        const pinDiff =
          Number(pinnedIds.has(String(b.value))) - Number(pinnedIds.has(String(a.value)));
        return pinDiff || a.label.localeCompare(b.label, "es");
      }),
    [items, pinnedIds]
  );

  const visibleItems = React.useMemo(() => {
    const term = normalize(query.trim());
    if (!term) return orderedItems;
    return orderedItems.filter(
      (item) => normalize(item.label).includes(term) || normalize(item.tag).includes(term)
    );
  }, [orderedItems, query]);

  const selectedCount = items.filter((item) => selectedIds.has(String(item.value))).length;
  const visibleUnselected = visibleItems.filter((item) => !selectedIds.has(String(item.value)));

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "10px",
        backgroundColor: theme.palette.background.paper,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: 34,
            px: 1.25,
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            "&:focus-within": { borderColor: theme.palette.grey[400] },
          }}
        >
          <Search size={15} color={theme.palette.text.secondary} />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            style={{
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              fontSize: "0.875rem",
              fontFamily: "inherit",
              color: theme.palette.text.primary,
              width: "100%",
            }}
          />
          {query && (
            <ButtonBase
              aria-label="Borrar búsqueda"
              onClick={() => setQuery("")}
              sx={{ borderRadius: "999px", color: "text.secondary", p: 0.25 }}
            >
              <X size={14} />
            </ButtonBase>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mt: 0.75,
            px: 0.5,
          }}
        >
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
            {plural(selectedCount, selectedWord[0], selectedWord[1])}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", color: "text.disabled" }}>
            <TextLinkButton
              onClick={() => onSelectMany(visibleUnselected)}
              disabled={visibleUnselected.length === 0}
            >
              {allLabel}
            </TextLinkButton>
            <Box component="span" sx={{ fontSize: "0.75rem" }}>·</Box>
            <TextLinkButton onClick={onClear} disabled={selectedCount === 0}>
              {noneLabel}
            </TextLinkButton>
          </Box>
        </Box>
      </Box>

      <Box ref={listRef} sx={{ maxHeight, overflowY: "auto", p: 0.5 }}>
        {visibleItems.map((item) => {
          const checked = selectedIds.has(String(item.value));
          return (
            <Box
              key={item.value}
              role="checkbox"
              aria-checked={checked}
              tabIndex={0}
              onClick={() => onToggle(item)}
              onKeyDown={(event) => {
                if (event.key === " " || event.key === "Enter") {
                  event.preventDefault();
                  onToggle(item);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 0.75,
                py: "2px",
                borderRadius: "8px",
                cursor: "pointer",
                "&:hover": { backgroundColor: theme.palette.action.hover },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: "-2px",
                },
              }}
            >
              <Checkbox checked={checked} onChange={() => { }} tabIndex={-1} />
              <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: "0.875rem" }}>
                {item.label}
              </Typography>
              {item.tag && (
                <Typography
                  noWrap
                  sx={{
                    flexShrink: 0,
                    maxWidth: "45%",
                    px: 0.75,
                    py: "1px",
                    borderRadius: "6px",
                    border: `1px solid ${theme.palette.divider}`,
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    color: "text.secondary",
                  }}
                >
                  {item.tag}
                </Typography>
              )}
            </Box>
          );
        })}
        {visibleItems.length === 0 && (
          <Typography
            sx={{ fontSize: "0.8125rem", color: "text.secondary", textAlign: "center", py: 3 }}
          >
            {isLoading ? loadingText : query ? "Sin resultados para esta búsqueda." : emptyText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

SelectionList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      tag: PropTypes.string,
    })
  ).isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSelectMany: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  emptyText: PropTypes.string.isRequired,
  loadingText: PropTypes.string,
  isLoading: PropTypes.bool,
  selectedWord: PropTypes.arrayOf(PropTypes.string).isRequired,
  allLabel: PropTypes.string.isRequired,
  noneLabel: PropTypes.string.isRequired,
  maxHeight: PropTypes.number,
};

const CheckRow = ({ checked, onChange, label, disabled }) => (
  <Box
    role="checkbox"
    aria-checked={checked}
    aria-disabled={disabled || undefined}
    tabIndex={disabled ? -1 : 0}
    onClick={() => !disabled && onChange(!checked)}
    onKeyDown={(event) => {
      if (!disabled && (event.key === " " || event.key === "Enter")) {
        event.preventDefault();
        onChange(!checked);
      }
    }}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.5 : 1,
      borderRadius: "6px",
    }}
  >
    <Checkbox checked={checked} onChange={() => { }} tabIndex={-1} disabled={disabled} />
    <Typography sx={{ fontSize: "0.875rem" }}>{label}</Typography>
  </Box>
);

CheckRow.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
};

const RadioRow = ({ checked, onSelect, label, disabled }) => (
  <Box
    role="radio"
    aria-checked={checked}
    aria-disabled={disabled || undefined}
    tabIndex={disabled ? -1 : 0}
    onClick={() => !disabled && onSelect()}
    onKeyDown={(event) => {
      if (!disabled && (event.key === " " || event.key === "Enter")) {
        event.preventDefault();
        onSelect();
      }
    }}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.45 : 1,
    }}
  >
    <Radio size="small" checked={checked} disabled={disabled} tabIndex={-1} sx={{ p: "4px" }} />
    <Typography sx={{ fontSize: "0.875rem" }}>{label}</Typography>
  </Box>
);

RadioRow.propTypes = {
  disabled: PropTypes.bool,
  checked: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.node.isRequired,
};

const toggleInList = (list, item) => {
  const current = list || [];
  const exists = current.some((entry) => String(entry.value) === String(item.value));
  return exists
    ? current.filter((entry) => String(entry.value) !== String(item.value))
    : [...current, item.option];
};

/** Sección completa de destinatarios del anuncio (agencias, consultores y clientes). */
export const AnnouncementAudienceSection = ({
  formState,
  setFormState,
  agencyOptions,
  isLoadingAgencies,
  clientOptions,
  isLoadingClients,
}) => {
  const scope = formState.agencyScope;

  const counts = React.useMemo(() => {
    const byType = { [AGENCY_SCOPE_SERSEO]: 0, [AGENCY_SCOPE_EXTERNAL]: 0 };
    agencyOptions.forEach((agency) => {
      if (agency.agencyType in byType) byType[agency.agencyType] += 1;
    });
    return byType;
  }, [agencyOptions]);

  const agencyIds = resolveAudienceAgencyIds(formState, agencyOptions);
  const hasAgencies = agencyIds === null || agencyIds.size > 0;
  const clientMode = formState.clientMode;
  const clientsEnabled = clientMode !== CLIENT_MODE_NONE;

  const selectedAgencyIds = React.useMemo(
    () => new Set((formState.targetAgencies || []).map((agency) => String(agency.value))),
    [formState.targetAgencies]
  );
  const selectedClientIds = React.useMemo(
    () => new Set((formState.targetClients || []).map((client) => String(client.value))),
    [formState.targetClients]
  );

  // Cualquier cambio de agencias descarta los clientes que quedan fuera.
  const updateAgencies = (updater) =>
    setFormState((prev) => {
      const next = updater(prev);
      return {
        ...next,
        targetClients: filterClientsByAgencyIds(
          next.targetClients,
          resolveAudienceAgencyIds(next, agencyOptions)
        ),
      };
    });

  const agencyItems = React.useMemo(
    () =>
      agencyOptions.map((agency) => ({
        value: agency.value,
        label: agency.label,
        tag: AGENCY_TYPE_TAG[agency.agencyType],
        option: agency,
      })),
    [agencyOptions]
  );

  const clientItems = React.useMemo(
    () =>
      clientOptions.map((client) => ({
        value: client.value,
        label: client.label,
        tag: client.agencyName || undefined,
        option: client,
      })),
    [clientOptions]
  );

  const loadingCount = (count) => (isLoadingAgencies ? undefined : count);

  return (
    <Box>
      <Box>
        <Label>Agencias</Label>
        <Box
          role="radiogroup"
          aria-label="Agencias destinatarias"
          sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mt: 0.5 }}
        >
          {[
            { value: AGENCY_SCOPE_ALL, label: "Todas", count: loadingCount(agencyOptions.length) },
            {
              value: AGENCY_SCOPE_SERSEO,
              label: "Red SERSEO",
              count: loadingCount(counts[AGENCY_SCOPE_SERSEO]),
              disabled: !isLoadingAgencies && counts[AGENCY_SCOPE_SERSEO] === 0,
            },
            {
              value: AGENCY_SCOPE_EXTERNAL,
              label: "Externas",
              count: loadingCount(counts[AGENCY_SCOPE_EXTERNAL]),
              disabled: !isLoadingAgencies && counts[AGENCY_SCOPE_EXTERNAL] === 0,
            },
            { value: AGENCY_SCOPE_MANUAL, label: "Personalizado" },
          ].map((option) => (
            <RadioRow
              key={option.value}
              checked={scope === option.value}
              disabled={option.disabled}
              onSelect={() => updateAgencies((prev) => ({ ...prev, agencyScope: option.value }))}
              label={
                <>
                  {option.label}
                  {option.count != null && (
                    <Box component="span" sx={{ ml: 0.5, color: "text.secondary" }}>
                      ({option.count})
                    </Box>
                  )}
                </>
              }
            />
          ))}
        </Box>
      </Box>
      {scope === AGENCY_SCOPE_MANUAL && (
        <Box sx={{ mt: 1 }}>
          <SelectionList
            items={agencyItems}
            selectedIds={selectedAgencyIds}
            onToggle={(item) =>
              updateAgencies((prev) => ({
                ...prev,
                targetAgencies: toggleInList(prev.targetAgencies, item),
              }))
            }
            onSelectMany={(items) =>
              updateAgencies((prev) => ({
                ...prev,
                targetAgencies: [...(prev.targetAgencies || []), ...items.map((i) => i.option)],
              }))
            }
            onClear={() => updateAgencies((prev) => ({ ...prev, targetAgencies: [] }))}
            searchPlaceholder="Buscar agencia…"
            emptyText="No hay agencias disponibles."
            loadingText="Cargando agencias…"
            isLoading={isLoadingAgencies}
            selectedWord={["seleccionada", "seleccionadas"]}
            allLabel="Todas"
            noneLabel="Ninguna"
          />
        </Box>
      )}

      <Box sx={{ mt: 2.5 }}>
        <Label>Además de los administradores de agencia</Label>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mt: 0.5 }}>
          <CheckRow
            checked={Boolean(formState.sendToConsultants)}
            onChange={(value) => setFormState((prev) => ({ ...prev, sendToConsultants: value }))}
            label="Incluir también a los consultores"
          />
          <CheckRow
            checked={clientsEnabled}
            disabled={!hasAgencies}
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                clientMode: value ? CLIENT_MODE_ALL : CLIENT_MODE_NONE,
                targetClients: [],
              }))
            }
            label="Enviar también a los clientes"
          />
        </Box>
        {clientsEnabled && hasAgencies && (
          <Box sx={{ ml: "30px", mt: 0.25 }}>
            <Box role="radiogroup" sx={{ display: "flex", flexWrap: "wrap", columnGap: 2 }}>
              <RadioRow
                checked={clientMode === CLIENT_MODE_ALL}
                onSelect={() =>
                  setFormState((prev) => ({ ...prev, clientMode: CLIENT_MODE_ALL, targetClients: [] }))
                }
                label={
                  <>
                    Todos los clientes de esas agencias
                    {!isLoadingClients && (
                      <Box component="span" sx={{ color: "text.secondary" }}>
                        {" "}({clientOptions.length})
                      </Box>
                    )}
                  </>
                }
              />
              <RadioRow
                checked={clientMode === CLIENT_MODE_SELECTED}
                onSelect={() =>
                  setFormState((prev) => ({ ...prev, clientMode: CLIENT_MODE_SELECTED }))
                }
                label="Elegir clientes"
              />
            </Box>
            {clientMode === CLIENT_MODE_SELECTED && (
              <Box sx={{ mt: 0.75 }}>
                <SelectionList
                  items={clientItems}
                  selectedIds={selectedClientIds}
                  onToggle={(item) =>
                    setFormState((prev) => ({
                      ...prev,
                      targetClients: toggleInList(prev.targetClients, item),
                    }))
                  }
                  onSelectMany={(items) =>
                    setFormState((prev) => ({
                      ...prev,
                      targetClients: [...(prev.targetClients || []), ...items.map((i) => i.option)],
                    }))
                  }
                  onClear={() => setFormState((prev) => ({ ...prev, targetClients: [] }))}
                  searchPlaceholder="Buscar cliente o agencia…"
                  emptyText="Las agencias destinatarias no tienen clientes."
                  loadingText="Cargando clientes…"
                  isLoading={isLoadingClients}
                  selectedWord={["seleccionado", "seleccionados"]}
                  allLabel="Todos"
                  noneLabel="Ninguno"
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

    </Box>
  );
};

AnnouncementAudienceSection.propTypes = {
  formState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  agencyOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoadingAgencies: PropTypes.bool,
  clientOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoadingClients: PropTypes.bool,
};

const AGENCY_SCOPE_SUMMARY = {
  [AGENCY_SCOPE_ALL]: "Todas",
  [AGENCY_SCOPE_SERSEO]: "Red SERSEO",
  [AGENCY_SCOPE_EXTERNAL]: "Externas",
};

const PREVIEW_MAX = 3;

const NamesPreview = ({ items }) => {
  const [expanded, setExpanded] = React.useState(false);
  const names = items.map((item) => item.label);
  if (names.length <= PREVIEW_MAX) return names.join(", ");
  return (
    <>
      {(expanded ? names : names.slice(0, PREVIEW_MAX)).join(", ")}{" "}
      <ButtonBase
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          color: "primary.main",
          fontWeight: 600,
          fontSize: "inherit",
          verticalAlign: "baseline",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {expanded ? "ver menos" : `y ${names.length - PREVIEW_MAX} más`}
      </ButtonBase>
    </>
  );
};

NamesPreview.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string })).isRequired,
};

const SummaryRow = ({ icon: Icon, label, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
    <Box sx={{ color: "text.secondary", mt: "2px", display: "flex" }}>
      <Icon size={16} />
    </Box>
    <Typography sx={{ width: 76, flexShrink: 0, fontSize: "0.8125rem", color: "text.secondary" }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0, fontSize: "0.875rem" }}>{children}</Box>
  </Box>
);

SummaryRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/** Resumen de solo lectura de un público de anuncio ya resuelto. */
export const AnnouncementAudienceSummary = ({
  title = "Se enviará a",
  scope,
  agencies,
  sendToConsultants,
  clientMode,
  clients,
  onEdit,
}) => {
  const { theme } = useTheme();

  const roles = ["Administradores"];
  if (sendToConsultants) roles.push("Consultores");
  if (clientMode !== CLIENT_MODE_NONE) roles.push("Clientes");

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "10px",
        backgroundColor: theme.palette.action.hover,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>{title}</Typography>
        {onEdit && <TextLinkButton onClick={onEdit}>Cambiar</TextLinkButton>}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <SummaryRow icon={Building2} label="Agencias">
          {scope === AGENCY_SCOPE_MANUAL ? <NamesPreview items={agencies} /> : AGENCY_SCOPE_SUMMARY[scope]}
          <Box component="span" sx={{ color: "text.secondary" }}> ({agencies.length})</Box>
        </SummaryRow>
        <SummaryRow icon={Users} label="Roles">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {roles.map((role) => (
              <Typography
                key={role}
                component="span"
                sx={{
                  px: 0.75,
                  py: "1px",
                  borderRadius: "6px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {role}
              </Typography>
            ))}
          </Box>
        </SummaryRow>
        {clientMode !== CLIENT_MODE_NONE && (
          <SummaryRow icon={UserRound} label="Clientes">
            {clientMode === CLIENT_MODE_ALL ? "Todos los de esas agencias" : <NamesPreview items={clients} />}
            <Box component="span" sx={{ color: "text.secondary" }}> ({clients.length})</Box>
          </SummaryRow>
        )}
      </Box>
    </Box>
  );
};

const namedItemsPropType = PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string }));

AnnouncementAudienceSummary.propTypes = {
  title: PropTypes.string,
  scope: PropTypes.oneOf([
    AGENCY_SCOPE_ALL,
    AGENCY_SCOPE_SERSEO,
    AGENCY_SCOPE_EXTERNAL,
    AGENCY_SCOPE_MANUAL,
  ]).isRequired,
  agencies: namedItemsPropType.isRequired,
  sendToConsultants: PropTypes.bool,
  clientMode: PropTypes.oneOf([CLIENT_MODE_NONE, CLIENT_MODE_ALL, CLIENT_MODE_SELECTED]).isRequired,
  clients: namedItemsPropType.isRequired,
  onEdit: PropTypes.func,
};
